import type { GridColDef } from '@mui/x-data-grid';
import { Box, IconButton } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { Currency, Label, RecurringRevenueReport } from '@vy/types';
import { isNullOrUndefined, shortenString, type ItemOf, type ValueOf } from '@vy/utils';
import { useCallback, useMemo } from 'react';
import { filterMonthColumns } from './filter-month-columns';
import { extractMonthlyColumnKeys as extractMonthlyColumnKeysImpl } from '../../utils/recurring-revenue-month-keys';
import { MONTHS_PER_YEAR, type MonthlyValueMode } from './recurring-revenue-view-mode';
import { ContractStatusChip } from '../../../../../components/design/ContractStatusChip';
import { Price } from '../../../../../components/design/Price';
import { VyTableCell } from '../../../../../components/new-design/table/vy-table/VyTableCell';
import { LabelsChips } from '../../../../../components/new-design/molecules/labels';
import { getFormattedNumberWithCurrencySymbol } from '../../../../../services/currency-symbol-mapper';
import {
  compareDatesDescending,
  compareOptionalDatesDescending,
  formatDate,
} from '../../../../../utils/date';
import { formatNumberWithPrecision } from '../../../../../utils/number';
import { TOTALS_ROW_ID } from '../../../reports-components/report-table-consts';
import { type ExpandedRecurringRevenueRow } from './recurring-revenue-report-row-expander';

const RECURRING_REVENUE_REPORT_KEYS = Object.freeze({
  EXPAND: 'expand',
  CUSTOMER_NAME: 'customerName',
  PRODUCT_NAME: 'productName',
  SUBSIDIARY_NAME: 'subsidiaryName',
  PARENT_NAME: 'parentName',
  LABELS: 'labels',
  CONTRACT_NAME: 'contractName',
  CONTRACT_START_DATE: 'contractStartDate',
  CONTRACT_END_DATE: 'contractEndDate',
  CONTRACT_STATUS: 'contractStatus',
  ORIGINAL_CURRENCY: 'originalCurrency',
  CURRENT_MRR_ORIGINAL_CURRENCY: 'currentMrrInOriginalCurrency',
  EXCHANGE_RATE: 'exchangeRate',
  CURRENT_MRR_USD: 'currentMrrUsd',
  TOTAL_ARR_USD: 'totalArrUsd',
  CONTRACT_ID: 'contractId',
} satisfies Record<string, string>);

export type RecurringRevenueReportColumnKey = ValueOf<typeof RECURRING_REVENUE_REPORT_KEYS>;

type RecurringRevenueReportRow = ExpandedRecurringRevenueRow;

export const TOTALS_CHILD_ROW_ID_PREFIX = '__totals_child_';

function isTotalsRow(row: RecurringRevenueReportRow | ItemOf<RecurringRevenueReport>): boolean {
  return row.contractId === TOTALS_ROW_ID;
}

function isTotalsChildRow(row: RecurringRevenueReportRow | ItemOf<RecurringRevenueReport>): boolean {
  return row.contractId.startsWith(TOTALS_CHILD_ROW_ID_PREFIX);
}

function isPinnedTotalsSection(row: RecurringRevenueReportRow | ItemOf<RecurringRevenueReport>): boolean {
  return isTotalsRow(row) || isTotalsChildRow(row);
}

type TotalsRowMeta = {
  __isExpanded?: boolean;
  __toggleExpand?: () => void;
};

const MONTH_COLUMN_PATTERN = /^[A-Za-z]{3} \d{4}$/;
export const MONTH_COLUMN_FIELD_PREFIX = 'month-';
export const RECURRING_REVENUE_REPORT_MONTH_COLUMN_CLASSNAME = 'vy-recurring-revenue-month-column';

// Re-export the view-mode types from their pure .ts module so existing consumers
// keep importing from this file unchanged.
export { MONTHS_PER_YEAR };
export type { MonthlyValueMode };

// Re-export the pure helper from its dedicated module so existing consumers
// keep importing from this file unchanged.
export const extractMonthlyColumnKeys = extractMonthlyColumnKeysImpl;

export const BASE_COLUMN_FIELDS = new Set(Object.values(RECURRING_REVENUE_REPORT_KEYS));

const MANDATORY_COLUMN_KEYS = new Set<string>([
  RECURRING_REVENUE_REPORT_KEYS.EXPAND,
  RECURRING_REVENUE_REPORT_KEYS.CUSTOMER_NAME,
]);

// Kept for callers that need the count (e.g. toolbar column-settings component).
export const MANDATORY_COLUMNS_COUNT = MANDATORY_COLUMN_KEYS.size;

const DEFAULT_VISIBLE_OPTIONAL_FIELDS = new Set<string>([
  RECURRING_REVENUE_REPORT_KEYS.PRODUCT_NAME,
  RECURRING_REVENUE_REPORT_KEYS.CONTRACT_NAME,
  RECURRING_REVENUE_REPORT_KEYS.CURRENT_MRR_USD,
  RECURRING_REVENUE_REPORT_KEYS.LABELS,
]);

export const OPTIONAL_BASE_COLUMN_FIELDS = Object.values(RECURRING_REVENUE_REPORT_KEYS).filter(
  (field) => !MANDATORY_COLUMN_KEYS.has(field) && !DEFAULT_VISIBLE_OPTIONAL_FIELDS.has(field),
);

const BASE_COLUMNS: GridColDef<RecurringRevenueReportRow>[] = [
  {
    field: RECURRING_REVENUE_REPORT_KEYS.CUSTOMER_NAME,
    headerName: 'Customer',
    flex: 1.3,
    minWidth: 220,
    headerAlign: 'left',
    align: 'left',
    valueGetter: (_value, row) => row.customerName,
    renderCell: ({ row }) => {
      // Movement breakdown rows under the pinned totals row — indented label.
      if (isTotalsChildRow(row)) {
        return <VyTableCell value={row.customerName} containerClassName="justify-start" style={{ marginLeft: '50%' }} />;
      }

      // Totals row carries its own expand toggle (the movement breakdown),
      // which used to live in the tree grouping column.
      if (isTotalsRow(row)) {
        const { __isExpanded, __toggleExpand } = row as RecurringRevenueReportRow & TotalsRowMeta;

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton size="small" onClick={__toggleExpand}>
              {__isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
            <VyTableCell value={row.customerName} containerClassName="justify-start font-bold" />
          </Box>
        );
      }

      return <VyTableCell value={row.customerName} containerClassName="justify-start" />;
    },
  },
  {
    field: RECURRING_REVENUE_REPORT_KEYS.CONTRACT_NAME,
    headerName: 'Contract',
    flex: 1,
    minWidth: 200,
    headerAlign: 'left',
    align: 'left',
    valueGetter: (_value, row) => (isPinnedTotalsSection(row) ? null : row.contractName || ''),
    renderCell: ({ row }) => {
      if (isPinnedTotalsSection(row)) return null;

      return <VyTableCell value={row.contractName || '--'} containerClassName="justify-start" />;
    },
  },
  {
    field: RECURRING_REVENUE_REPORT_KEYS.PRODUCT_NAME,
    headerName: 'Product',
    flex: 1,
    minWidth: 200,
    headerAlign: 'left',
    align: 'left',
    valueGetter: (_value, row) => (isPinnedTotalsSection(row) ? null : row.productName),
    renderCell: ({ row }) => {
      if (isPinnedTotalsSection(row)) return null;

      return <VyTableCell value={row.productName || '--'} containerClassName="justify-start" />;
    },
  },
  {
    field: RECURRING_REVENUE_REPORT_KEYS.SUBSIDIARY_NAME,
    headerName: 'Subsidiary',
    flex: 1,
    minWidth: 180,
    headerAlign: 'left',
    align: 'left',
    renderCell: ({ row }) => {
      if (isPinnedTotalsSection(row)) return null;

      return (
        <VyTableCell
          value={isNullOrUndefined(row.subsidiaryName) ? '--' : row.subsidiaryName}
          containerClassName="justify-start"
        />
      )
    },
  },
  {
    field: RECURRING_REVENUE_REPORT_KEYS.PARENT_NAME,
    headerName: 'Parent Name',
    flex: 1,
    minWidth: 200,
    headerAlign: 'left',
    align: 'left',
    renderCell: ({ row }) => {
      if (isPinnedTotalsSection(row)) return null;

      return (
        <VyTableCell
          value={isNullOrUndefined(row.parentName) ? '--' : row.parentName}
          containerClassName="justify-start"
        />
      );
    },
  },
  {
    field: RECURRING_REVENUE_REPORT_KEYS.LABELS,
    headerName: 'Labels',
    flex: 1,
    minWidth: 180,
    headerAlign: 'left',
    align: 'left',
    sortable: false,
    renderCell: ({ row }) => {
      if (isPinnedTotalsSection(row)) return null;

      const labels = (row as Record<string, unknown>).labels as { id: string; name: string; color: string; backgroundColor: string }[] | undefined;

      if (!labels || labels.length === 0) {
        return <VyTableCell value="--" containerClassName="justify-start" />;
      }

      return <LabelsChips labels={labels as Label[]} />;
    },
    valueGetter: (_value, row) => {
      const labels = (row as Record<string, unknown>).labels as { name: string }[] | undefined;
      return labels?.map((l) => l.name).join(', ') ?? '';
    },
  },
  {
    field: RECURRING_REVENUE_REPORT_KEYS.CONTRACT_ID,
    headerName: 'Contract ID',
    flex: 0.8,
    minWidth: 140,
    headerAlign: 'left',
    align: 'left',
    valueGetter: (_value, row) => (isPinnedTotalsSection(row) ? null : row.contractId),
    renderCell: ({ row }) => {
      if (isPinnedTotalsSection(row)) return null;

      const stringToDisplay = isNullOrUndefined(row.contractId) ? '--' : shortenString(row.contractId, 5);
      return <VyTableCell value={stringToDisplay} containerClassName="justify-start" />;
    },
  },
  {
    field: RECURRING_REVENUE_REPORT_KEYS.CONTRACT_START_DATE,
    headerName: 'Contract Start Date',
    flex: 1,
    minWidth: 160,
    headerAlign: 'left',
    align: 'left',
    valueGetter: (_value, row) => isPinnedTotalsSection(row) ? null : new Date(row.contractStartDate),
    renderCell: ({ row }) => {
      if (isPinnedTotalsSection(row)) return null;

      return formatDate(new Date(row.contractStartDate));
    },
    valueFormatter: value => (value ? formatDate(value as Date) : ''),
    sortComparator: (firstDate, secondDate) =>
      compareDatesDescending(firstDate as Date, secondDate as Date),
  },
  {
    field: RECURRING_REVENUE_REPORT_KEYS.CONTRACT_END_DATE,
    headerName: 'Contract End Date',
    flex: 1,
    minWidth: 160,
    headerAlign: 'left',
    align: 'left',
    valueGetter: (_value, row) => isPinnedTotalsSection(row) || isNullOrUndefined(row.contractEndDate)
      ? null
      : new Date(row.contractEndDate),
    renderCell: ({ row }) => {
      if (isPinnedTotalsSection(row)) return null;

      return isNullOrUndefined(row.contractEndDate) ? '--' : formatDate(new Date(row.contractEndDate));
    },
    valueFormatter: value => (value ? formatDate(value as Date) : ''),
    sortComparator: (firstDate, secondDate) =>
      compareOptionalDatesDescending(firstDate as Date | null, secondDate as Date | null),
  },
  {
    field: RECURRING_REVENUE_REPORT_KEYS.CONTRACT_STATUS,
    headerName: 'Contract Status',
    flex: 1,
    minWidth: 180,
    headerAlign: 'left',
    align: 'left',
    renderCell: ({ row }) => {
      if (isPinnedTotalsSection(row)) return null;

      return (
        <ContractStatusChip
          status={row.contractStatus}
          endDate={isNullOrUndefined(row.contractEndDate) ? undefined : new Date(row.contractEndDate)}
        />
      );
    },
  },
  {
    field: RECURRING_REVENUE_REPORT_KEYS.ORIGINAL_CURRENCY,
    headerName: 'Original Currency',
    flex: 0.8,
    minWidth: 140,
    headerAlign: 'left',
    align: 'left',
    renderCell: ({ row }) => {
      if (isPinnedTotalsSection(row)) return null;

      return <VyTableCell value={row.originalCurrency} style={{ justifyContent: 'center' }} />;
    },
  },
  {
    field: RECURRING_REVENUE_REPORT_KEYS.CURRENT_MRR_ORIGINAL_CURRENCY,
    headerName: 'Current MRR (Original Currency)',
    flex: 1.2,
    minWidth: 220,
    headerAlign: 'left',
    align: 'left',
    renderCell: ({ row }) => {
      if (isPinnedTotalsSection(row)) return null;

      return (
        <VyTableCell
          value={getFormattedNumberWithCurrencySymbol(row.originalCurrency as Currency, row.currentMrrInOriginalCurrency)}
          containerClassName="justify-start"
        />
      );
    },
    valueFormatter: value => formatNumberWithPrecision(Number(value ?? 0), 2),
  },
  {
    field: RECURRING_REVENUE_REPORT_KEYS.EXCHANGE_RATE,
    headerName: 'Exchange Rate To $',
    flex: 1,
    minWidth: 180,
    headerAlign: 'left',
    align: 'left',
    renderCell: ({ row }) => {
      if (isPinnedTotalsSection(row)) return null;

      return (
        <VyTableCell
          value={isNullOrUndefined(row.exchangeRate) ? '--' : formatNumberWithPrecision(row.exchangeRate, 4)}
          containerClassName="justify-start"
        />
      );
    },
    valueFormatter: value => isNullOrUndefined(value) ? '' : formatNumberWithPrecision(Number(value ?? 0), 4),
  },
  {
    field: RECURRING_REVENUE_REPORT_KEYS.CURRENT_MRR_USD,
    headerName: 'Current MRR $',
    flex: 1,
    minWidth: 180,
    headerAlign: 'left',
    align: 'left',
    renderCell: ({ row }) => {
      if (isTotalsChildRow(row)) return null;

      return (
        <Price currency="USD" align="left" fractionDigits={2}>
          {row.currentMrrUsd}
        </Price>
      );
    },
    valueFormatter: value => formatNumberWithPrecision(Number(value ?? 0), 2),
  },
];

export function useRecurringRevenueReportColumns(
  rows: RecurringRevenueReport,
  fromDate?: Date | null,
  toDate?: Date | null,
  viewMode: MonthlyValueMode = 'arr',
): GridColDef<RecurringRevenueReportRow>[] {
  const factor = viewMode === 'arr' ? MONTHS_PER_YEAR : 1;

  const { customerFieldKeys, contractFieldKeys } = useMemo(
    () => extractCustomFieldKeys(rows),
    [rows],
  );

  const monthlyColumnKeys = useMemo(() => extractMonthlyColumnKeys(rows), [rows]);

  const filteredMonthlyColumnKeys = useMemo(
    () => filterMonthColumns(monthlyColumnKeys, fromDate, toDate),
    [monthlyColumnKeys, fromDate, toDate],
  );

  const buildCustomFieldCell = useCallback((key: string) => {
    return ({ row }: { row: RecurringRevenueReportRow }) => {
      const value = (row as Record<string, unknown>)[key];
      const display =
        value === null || typeof value === 'undefined' || value === '' ? '--' : String(value);
      return <VyTableCell value={display} containerClassName="justify-start" />;
    };
  }, []);

  const buildCustomColumns = useCallback(
    (keys: string[]) =>
      keys.map<GridColDef<RecurringRevenueReportRow>>((key) => ({
        field: key,
        headerName: key,
        flex: 1,
        minWidth: 180,
        headerAlign: 'left',
        align: 'left',
        renderCell: buildCustomFieldCell(key),
      })),
    [buildCustomFieldCell],
  );

  const customerCustomFieldColumns = useMemo(
    () => buildCustomColumns(customerFieldKeys),
    [buildCustomColumns, customerFieldKeys],
  );

  const contractCustomFieldColumns = useMemo(
    () => buildCustomColumns(contractFieldKeys),
    [buildCustomColumns, contractFieldKeys],
  );

  const buildMonthlyCell = useCallback(
    (monthKey: string) => {
      return ({ row }: { row: RecurringRevenueReportRow }) => {
        if (isTotalsChildRow(row)) {
          const value = getMonthlyValue(row, monthKey) * factor;

          if (value === 0) {
            return <VyTableCell value="--" containerClassName="justify-start" />;
          }

          return (
            <Price currency="USD" align="left" fractionDigits={2}>
              {value}
            </Price>
          );
        }

        return (
          <Price currency="USD" align="left" fractionDigits={2}>
            {getMonthlyValue(row, monthKey) * factor}
          </Price>
        );
      };
    },
    [factor],
  );

  const monthlyColumns = useMemo(
    () =>
      filteredMonthlyColumnKeys.map<GridColDef<RecurringRevenueReportRow>>((monthKey) => {
        const field = buildMonthFieldId(monthKey);

        return {
          field,
          headerName: monthKey,
          flex: 1,
          minWidth: 160,
          type: 'number',
          headerAlign: 'left',
          align: 'left',
          valueGetter: (_value, row) => getMonthlyValue(row, monthKey) * factor,
          valueFormatter: value => formatNumberWithPrecision(Number(value ?? 0), 2),
          headerClassName: RECURRING_REVENUE_REPORT_MONTH_COLUMN_CLASSNAME,
          cellClassName: RECURRING_REVENUE_REPORT_MONTH_COLUMN_CLASSNAME,
          renderCell: buildMonthlyCell(monthKey),
        };
      }),
    [filteredMonthlyColumnKeys, buildMonthlyCell, factor],
  );

  return useMemo(() => {
    const parentIdx = BASE_COLUMNS.findIndex(
      (c) => c.field === RECURRING_REVENUE_REPORT_KEYS.PARENT_NAME,
    );
    const statusIdx = BASE_COLUMNS.findIndex(
      (c) => c.field === RECURRING_REVENUE_REPORT_KEYS.CONTRACT_STATUS,
    );
    const beforeParent = parentIdx >= 0 ? BASE_COLUMNS.slice(0, parentIdx + 1) : BASE_COLUMNS;
    const betweenParentAndStatus =
      parentIdx >= 0 && statusIdx >= 0 ? BASE_COLUMNS.slice(parentIdx + 1, statusIdx + 1) : [];
    const afterStatus = statusIdx >= 0 ? BASE_COLUMNS.slice(statusIdx + 1) : [];

    return [
      ...beforeParent,
      ...customerCustomFieldColumns,
      ...betweenParentAndStatus,
      ...contractCustomFieldColumns,
      ...afterStatus,
      ...monthlyColumns,
    ];
  }, [customerCustomFieldColumns, contractCustomFieldColumns, monthlyColumns]);
}

function getMonthlyValue(row: RecurringRevenueReportRow | ItemOf<RecurringRevenueReport>, key: string): number {
  const value = (row as Record<string, unknown>)[key];

  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'object' && value !== null && 'totalMrr' in value) {
    return (value as { totalMrr: number }).totalMrr;
  }

  return 0;
}

function buildMonthFieldId(monthKey: string) {
  return `${MONTH_COLUMN_FIELD_PREFIX}${monthKey.toLowerCase().replace(/\s+/g, '-')}`;
}

function extractCustomFieldKeys(
  rows: RecurringRevenueReport,
): { customerFieldKeys: string[]; contractFieldKeys: string[] } {
  if (rows.length === 0) {
    return { customerFieldKeys: [], contractFieldKeys: [] };
  }

  const firstRow = rows[0] as Record<string, unknown>;
  const explicitCustomerKeys = Array.isArray(firstRow.customerCustomFieldOrder)
    ? (firstRow.customerCustomFieldOrder as string[])
    : undefined;
  const explicitContractKeys = Array.isArray(firstRow.contractCustomFieldOrder)
    ? (firstRow.contractCustomFieldOrder as string[])
    : undefined;

  if (explicitCustomerKeys && explicitContractKeys) {
    return {
      customerFieldKeys: explicitCustomerKeys.slice(),
      contractFieldKeys: explicitContractKeys.slice(),
    };
  }

  const baseKeys = new Set<string>(Object.values(RECURRING_REVENUE_REPORT_KEYS));
  const customerKeys = new Set<string>();
  const contractKeys = new Set<string>();

  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (key.startsWith('__')) {
        continue;
      }
      if (baseKeys.has(key)) {
        continue;
      }
      if (MONTH_COLUMN_PATTERN.test(key)) {
        continue;
      }
      if (explicitContractKeys?.includes(key)) {
        contractKeys.add(key);
        continue;
      }
      customerKeys.add(key);
    }
  }

  return {
    customerFieldKeys: Array.from(customerKeys).sort(),
    contractFieldKeys: Array.from(contractKeys).sort(),
  };
}
